<script setup lang="ts">
import { defineProps } from "vue";
import { BlogCardItem } from "@/types/blogs";
import { useLangStore } from "@/stores/langStore";
import OpenButton from "@/components/buttons/OpenButton.vue";
import BlogCardTag from "@/views/Blogs/components/BlogCard/BlogCardTag.vue";
import { useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  blog: BlogCardItem;
}>();

const { t } = useI18n();
const router = useRouter();
const langStore = useLangStore();

function openBlog() {
  router.push({ name: ROUTE_NAMES.BLOG_DETAIL, params: { id: props.blog.id } });
}
</script>

<template>
  <div class="card-container group/container cursor-pointer" @click="openBlog">
    <div class="relative w-full xl:h-[290px] md:h-[180px] mb-2 md:mb-4">
      <img
        :src="props.blog.cardImageUrl"
        alt="blog card image"
        class="w-full h-full rounded-[20px] object-cover aspect-video"
      />
      <div
        class="absolute bottom-[10px] left-[10px] mr-[10px] gap-2 flex flex-wrap max-w-[calc(100%-20px)]"
      >
        <BlogCardTag
          v-for="tag in props.blog.tags"
          :tag-name="tag[langStore.current] || tag[langStore.fallbackLang]"
          :bg-image="props.blog.cardImageUrl"
          :key="tag.id"
        />
      </div>
    </div>
    <!-- TITLE -->
    <p
      class="sf-pro font-semibold text-base leading-[21px] xl:text-2xl xl:leading-8 max-h-[84px] md:max-h-14 xl:max-h-max line-clamp-3 md:line-clamp-2 xl:line-clamp-none text-brown group-hover/container:text-active text-left mb-2 transition-colors duration-300 ease-in-out"
    >
      {{
        props.blog.titles[langStore.current] ||
        props.blog.titles[langStore.fallbackLang]
      }}
    </p>
    <!-- CREATED DATE -->
    <p
      class="sf-pro text-sm font-normal leading-[21px] md:text-base md:leading-[24px] xl:leading-[28px] text-muted text-left mb-2"
    >
      {{ props.blog.createdAt }}
    </p>
    <!-- SHORT DESCRIPTION -->
    <p
      class="sf-pro text-sm font-normal leading-[21px] md:text-base md:leading-[24px] xl:text-2xl xl:leading-[28px] text-brown text-left mb-4 md:line-clamp-3 line-clamp-4"
    >
      {{
        props.blog.shortDescriptions[langStore.current] ||
        props.blog.shortDescriptions[langStore.fallbackLang]
      }}
    </p>
    <OpenButton
      :label="t('blog.read_more')"
      type="brown-outline"
      hover-container="active-outline"
      hover-type="active"
    />
  </div>
</template>

<style scoped>
.card-container {
  @apply xl:max-h-[570px] xl:max-w-[396px] md:max-h-[422px] md:max-w-[246px];
}
</style>
